import React, { useState, useEffect } from 'react';
import api from '../../../api';
import styles from './PushNotificationsCenter.module.css';
import AppBar from '../../../base/components/AppBar/AppBar';

import useLazyQuery from '../../../surveys/hooks/useLazyQuery';

import PushNotificationsList from '../PushNotificationsList/PushNotificationsList';
import SendPushNotifications from '../SendPushNotifications/SendPushNotifications';
import PushNotificationDetails from '../PushNotificationDetails/PushNotificationDetails';
import useUserback from '../../../hooks/useUserback';

import { GET_COMMUNITIES } from '../../../graphql/Survey';

const FILTER_CONSTANTS = {
  MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
  RANGE: 'RANGE',
  SINGLE_OPTION: 'SINGLE_OPTION',
  DEFAULT_PLACEHOLDER: 'Please select value'
};

const PushNotificationsCenter = props => {
  useUserback({});

  const [isListFetching, setIsListFetching] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [pushNotificationDetails, setPushNotificationDetails] = useState(false);
  const [filterConfiguration, setFilterConfigurationGenerator] = useState(null);
  const [pushNotifications, setPushNotifications] = useState([]);
  const [
    pushNotificationToSendAgain,
    setPushNotificationToSendAgain
  ] = useState({});

  /* eslint-disable */
  const selectedAccountManagementRecord =
    props &&
    props.accountManagement &&
    props.accountManagement.selectedAccountManagementRecord &&
    props.accountManagement.selectedAccountManagementRecord.value;
  /* eslint-enable */

  const searchCommunitiesApi = useLazyQuery(GET_COMMUNITIES, {
    fetchPolicy: 'no-cache'
  });

  const filterConfigurationGenerator = geodata => ({
    users: {
      type: FILTER_CONSTANTS.MULTIPLE_CHOICE,
      label: 'Users',
      name: 'users',
      search: true,
      fetchOptions: async (page, search) => {
        const users = await api().getAllUsers(0, (page + 1) * 10, search);
        if (users.json.data) {
          return users.json.data.map(option => ({
            value: option.id,
            label: option.attributes.email
          }));
        }
        return null;
      }
    },
    communities: {
      type: FILTER_CONSTANTS.MULTIPLE_CHOICE,
      label: 'Communities',
      name: 'communities',
      search: true,
      fetchOptions: async (page, search) => {
        const searchResults = await searchCommunitiesApi({
          variables: {
            start: 0,
            end: (page + 1) * 10,
            search,
            salt: Math.random().toString()
          }
        });
        return searchResults &&
          searchResults.data &&
          searchResults.data.getCommunities &&
          searchResults.data.getCommunities.communityTags
          ? searchResults.data.getCommunities.communityTags.map(o => ({
              label: o,
              value: o
            }))
          : [];
      }
    },
    campaigns: {
      type: FILTER_CONSTANTS.SINGLE_OPTION,
      label: 'Surveys',
      name: 'campaigns',
      search: true,
      fetchOptions: async (page, search) => {
        const filter = `&campaignFilter[campaignNameSearch]=${search}`;

        const campaigns = await api().getCampaigns(
          selectedAccountManagementRecord,
          0,
          (page + 1) * 10,
          filter
        );
        if (campaigns.campaigns) {
          return campaigns.campaigns.map(option => ({
            value: option.id,
            label: option.name
          }));
        }
        return null;
      }
    },
    age: {
      type: FILTER_CONSTANTS.RANGE,
      label: 'Age',
      name: 'age',
      gteLabel: 'Younger than',
      lteLabel: 'Older than'
    },
    gender: {
      type: FILTER_CONSTANTS.MULTIPLE_CHOICE,
      label: 'Gender',
      name: 'gender',
      options: [
        {
          value: 'Male',
          label: 'Male'
        },
        {
          value: 'Female',
          label: 'Female'
        },
        {
          value: 'Other',
          label: 'Other'
        }
      ]
    },
    language: {
      type: FILTER_CONSTANTS.MULTIPLE_CHOICE,
      label: 'Language',
      name: 'language',
      options: [
        {
          label: 'Dutch',
          value: 'Dutch'
        },
        {
          label: 'French',
          value: 'French'
        }
      ]
    },
    activity: {
      type: FILTER_CONSTANTS.SINGLE_OPTION,
      label: 'Activity',
      options: [
        {
          label: 'Active today',
          value: 'activeToday'
        },
        {
          label: 'Active in past 7 days',
          value: 'activePast7Days'
        },
        {
          label: 'Active in past 30 days',
          value: 'activePast30Days'
        },
        {
          label: 'Inactive today',
          value: 'inactiveToday'
        },
        {
          label: 'Inactive in past 7 days',
          value: 'inactivePast7Days'
        },
        {
          label: 'Inactive in past 30 days',
          value: 'inactivePast30Days'
        }
      ],
      name: 'activity'
    },
    regions: {
      type: FILTER_CONSTANTS.MULTIPLE_CHOICE,
      label: 'Regions',
      name: 'regions',
      search: true,
      options: geodata.regions
    },
    postCodes: {
      type: FILTER_CONSTANTS.MULTIPLE_CHOICE,
      label: 'Post Codes',
      name: 'postCodes',
      search: true,
      fetchOptions: async (page, search) => {
        const postCodes = geodata.postCodes.filter(postCode =>
          postCode.value.includes(search)
        );
        return postCodes.slice(0, (page + 1) * 10);
      }
    },
    places: {
      type: FILTER_CONSTANTS.MULTIPLE_CHOICE,
      label: 'Places',
      name: 'places',
      search: true,
      fetchOptions: async (page, search) => {
        const places = geodata.places.filter(place =>
          place.label.includes(search)
        );
        const placesSlice = places.slice(0, (page + 1) * 10);

        return placesSlice.filter(
          (thing, index, self) =>
            index ===
            self.findIndex(
              t => t.value === thing.value && t.label === thing.label
            )
        );
      }
    }
  });

  const processGeodata = geodata => {
    const regions = [];
    const postCodes = [];
    const places = [];

    Object.keys(geodata).map(region => {
      regions.push({
        value: region,
        label: region
      });

      Object.keys(geodata[region].postcodes).map(postcode => {
        postCodes.push({
          value: postcode,
          label: postcode
        });

        geodata[region].postcodes[postcode].forEach(singlePlace => {
          const value = Object.keys(singlePlace)[0];
          const label = singlePlace[value];
          const existingPlace = places.filter(
            place => place.value === singlePlace.value
          );

          if (!existingPlace || existingPlace.length === 0) {
            places.push({
              value,
              label
            });
          }
        });

        return null;
      });
      return null;
    });

    return {
      regions,
      postCodes,
      places
    };
  };

  useEffect(() => {
    const getPushNotificationsConf = async () => {
      setIsFetching(true);

      const configuration = await api().getPushNotificationsConf();
      const geodata = processGeodata(configuration.json.geodata);

      setFilterConfigurationGenerator(filterConfigurationGenerator(geodata));

      setIsFetching(false);
    };

    getPushNotificationsConf();
  }, []);

  if (isFetching) {
    return (
      <div>
        <AppBar
          {...props}
          appBarProperties={{}}
          isFetching={isFetching}
          key="app-bar"
        />
        <div className={styles.pageContainer}>
          <div className={styles.pageHeader}>
            <div className={styles.header1}>Push Notifications Center</div>
          </div>
          <div className={styles.pageContent}>
            <div
              className={`${
                styles.pageContentListPlaceholder
              } loader-container`}
            />
            <div
              className={`${
                styles.pageContentMainPlaceholder
              } loader-container`}
            />
          </div>
        </div>
      </div>
    );
  }

  if (filterConfiguration && !isFetching) {
    return (
      <div>
        <AppBar
          {...props}
          appBarProperties={{}}
          isFetching={isFetching}
          isAuthenticated
          isAdmin
          onGoToPortalPage={() => props.history.push('/')}
          key="app-bar"
        />
        <div className={styles.pageContainer}>
          <div className={styles.pageHeader}>
            <div className={styles.header1}>Push Notifications Center</div>
          </div>
          <div className={styles.pageContent}>
            <PushNotificationsList
              pushNotificationDetails={pushNotificationDetails}
              setPushNotificationDetails={setPushNotificationDetails}
              isListFetching={isListFetching}
              setIsListFetching={setIsListFetching}
              pushNotifications={pushNotifications}
              setPushNotifications={setPushNotifications}
            />
            <div
              className={`${styles.notificationDetailsContainer} ${
                styles.whiteContainer
              }`}
            >
              {pushNotificationDetails ? (
                <PushNotificationDetails
                  pushNotificationDetails={pushNotificationDetails}
                  setPushNotificationDetails={setPushNotificationDetails}
                  setPushNotificationToSendAgain={
                    setPushNotificationToSendAgain
                  }
                />
              ) : (
                <SendPushNotifications
                  filterConfiguration={filterConfiguration}
                  FILTER_CONSTANTS={FILTER_CONSTANTS}
                  activeFilters={activeFilters}
                  setActiveFilters={setActiveFilters}
                  isListFetching={isListFetching}
                  pushNotifications={pushNotifications}
                  setPushNotifications={setPushNotifications}
                  pushNotificationToSendAgain={pushNotificationToSendAgain}
                  setPushNotificationToSendAgain={
                    setPushNotificationToSendAgain
                  }
                  setPushNotificationDetails={setPushNotificationDetails}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export default PushNotificationsCenter;
